import React, { useState, useEffect } from "react";
import { Input, Checkbox, Collapse, Badge, Empty, Alert, Tooltip } from "antd";
import { SearchOutlined, TeamOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { assignUserRolePractitioner, removeUserRolePractitioner } from "../../../api/admin/rolesApi";
import { RoleGroups } from "../../../api/admin/rolesApi";
import { Role } from "../../../types/admin/RoleTypes";
import { User } from "../../../types/admin/UserTypes";
import { TeamMember } from "../../../types/admin/TeamTypes";

const { Panel } = Collapse;

interface RolesTabProps {
	user: User | TeamMember;
	setUser: React.Dispatch<React.SetStateAction<any>>;
	roles: Role[];
	availableRoles?: string[];
	mode: "admin" | "team";
	handleAutoSave: () => void;
}

const RoleCategoryDisplayNames: Record<string, string> = {
	ADMINISTRATIVE: "Administrative Roles",
	CLINICAL: "Clinical Roles",
	SUPPORT: "Support Staff Roles",
	PATIENT: "Patient Roles",
	RESEARCH: "Research & Compliance Roles",
	LAB: "Laboratory Roles",
	GENE_CODES: "Gene.Codes Access",
	LEGACY: "Other Roles",
};

const RolesTab: React.FC<RolesTabProps> = ({ user, setUser, roles, availableRoles = [], handleAutoSave }) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [categorizedRoles, setCategorizedRoles] = useState<Record<string, Role[]>>({});
	const [activeKeys, setActiveKeys] = useState<string[]>(["ADMINISTRATIVE", "CLINICAL"]);

	useEffect(() => {
		if (!roles?.length) return;

		const isSubscriptionGated = availableRoles.length > 0;
		const allowedSet = new Set(availableRoles);

		const categorized: Record<string, Role[]> = {
			ADMINISTRATIVE: [],
			CLINICAL: [],
			SUPPORT: [],
			PATIENT: [],
			RESEARCH: [],
			LAB: [],
			GENE_CODES: [],
			LEGACY: [],
		};

		roles.forEach((role) => {
			if (isSubscriptionGated && !allowedSet.has(role.name)) return;
			let assigned = false;
			Object.entries(RoleGroups).forEach(([category, roleNames]) => {
				if ((roleNames as string[]).includes(role.name)) {
					categorized[category].push(role);
					assigned = true;
				}
			});
			if (!assigned) categorized.LEGACY.push(role);
		});

		setCategorizedRoles(categorized);
	}, [roles, availableRoles]);

	const handleRoleChange = async (checked: boolean, role: Role) => {
		setIsSubmitting(true);
		try {
			const userData = { ...user };
			if (checked) {
				await assignUserRolePractitioner(user.id, role.name);
				userData.roles = [...user.roles, role];
			} else {
				await removeUserRolePractitioner(user.id, role.name);
				userData.roles = user.roles.filter((r) => r.name !== role.name);
			}
			setUser(userData);
		} catch (err) {
			const response = (err as { response?: { status?: number; data?: { error?: string; used?: number; total?: number } } })?.response;
			const status = response?.status;
			const body = response?.data;
			console.error("roles.update.failed", { status, role: role.name });
			if (status === 422 && body?.error === "seat_limit_reached") {
				setError(`No seat is available for a clinical role (${body.used} of ${body.total} seats used). Contact support via the Help page to add seats.`);
			} else {
				setError("Failed to update roles.");
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const filteredCategories = Object.entries(categorizedRoles).reduce(
		(acc, [category, categoryRoles]) => {
			const filtered = searchTerm
				? categoryRoles.filter((r) => r.name.toLowerCase().includes(searchTerm.toLowerCase()))
				: categoryRoles;
			if (filtered.length > 0) acc[category] = filtered;
			return acc;
		},
		{} as Record<string, Role[]>
	);

	const hasNoResults =
		searchTerm.length > 0 && Object.values(filteredCategories).every((r) => r.length === 0);

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			{error && (
				<Alert message={error} type="error" closable onClose={() => setError("")} />
			)}

			<Input
				value={searchTerm}
				onChange={(e) => setSearchTerm(e.target.value)}
				placeholder="Search roles…"
				prefix={<SearchOutlined />}
				allowClear
			/>

			{hasNoResults ? (
				<Empty description="No roles match your search." />
			) : (
				<Collapse
					activeKey={activeKeys}
					onChange={(keys) => setActiveKeys(keys as string[])}
					ghost
				>
					{Object.entries(filteredCategories).map(([category, categoryRoles]) =>
						category !== "LEGACY" && categoryRoles.length > 0 ? (
							<Panel
								key={category}
								header={
									<span style={{ display: "flex", alignItems: "center", gap: 8 }}>
										<TeamOutlined />
										<span style={{ fontWeight: 600 }}>
											{RoleCategoryDisplayNames[category] ?? category}
										</span>
										<Badge count={categoryRoles.length} style={{ backgroundColor: "var(--mg-primary-bg)", color: "var(--mg-primary)" }} />
									</span>
								}
							>
								<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8, paddingTop: 8 }}>
									{categoryRoles.map((role) => (
										<div key={role.name} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 6, background: "var(--mg-bg-subtle)" }}>
											<Checkbox
												checked={user.roles?.some((r) => r.name === role.name) ?? false}
												onChange={(e) => handleRoleChange(e.target.checked, role)}
												disabled={isSubmitting}
											/>
											<span style={{ fontSize: 13, cursor: "pointer", flex: 1 }}>{role.name}</span>
										</div>
									))}
								</div>
							</Panel>
						) : null
					)}
					{/* Legacy / uncategorized */}
					{filteredCategories.LEGACY?.length > 0 && (
						<Panel
							key="LEGACY"
							header={
								<span style={{ display: "flex", alignItems: "center", gap: 8 }}>
									<TeamOutlined />
									<span style={{ fontWeight: 600 }}>{RoleCategoryDisplayNames.LEGACY}</span>
									<Badge count={filteredCategories.LEGACY.length} style={{ backgroundColor: "var(--mg-bg-subtle)", color: "var(--mg-text-secondary)" }} />
								</span>
							}
						>
							<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8, paddingTop: 8 }}>
								{filteredCategories.LEGACY.map((role) => (
									<div key={role.name} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 6, background: "var(--mg-bg-subtle)" }}>
										<Checkbox
											checked={user.roles?.some((r) => r.name === role.name) ?? false}
											onChange={(e) => handleRoleChange(e.target.checked, role)}
											disabled={isSubmitting}
										/>
										<span style={{ fontSize: 13, cursor: "pointer", flex: 1 }}>{role.name}</span>
									</div>
								))}
							</div>
						</Panel>
					)}
				</Collapse>
			)}
		</div>
	);
};

export default RolesTab;
